import { Dialog, Transition } from '@headlessui/react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Fragment } from 'react';
import * as Yup from 'yup';

import { addWorkspaceUser } from '../../services/workspaceSettingsService';
import { getPermittedRoleOptions } from '../../utils/roleUtils';

import type {
  AddUserFormValues,
  AddUserModalProps,
} from './AddUserModal.types';

import { useAppSelector } from '@store/hooks';

const emailSchema = Yup.object({
  email: Yup.string()
    .email('Enter a valid email address.')
    .required('Email is required.'),
  applicationRole: Yup.string().optional(),
});

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const workspaceRoles: string[] = useAppSelector(
    (s: any) => s.auth?.user?.workspaceContext?.roles ?? []
  );

  const roleOptions = getPermittedRoleOptions(workspaceRoles);

  const handleSubmit = async (
    values: AddUserFormValues,
    { setSubmitting, setFieldError, resetForm }: any
  ) => {
    try {
      const request = {
        email: values.email.trim(),
        applicationRole: values.applicationRole || undefined,
      } as { email: string; applicationRole?: 'Admin' | 'Manager' | 'User' };

      const { status, data } = await addWorkspaceUser(request);

      resetForm();
      onClose();

      if (status === 201) {
        onSuccess(`${data.email} has been added to the workspace.`);
      } else {
        onSuccess(`An invitation has been sent to ${data.email}.`);
      }
    } catch (err: any) {
      if (err?.status === 409) {
        if (err.code === 'USER_ALREADY_MEMBER') {
          setFieldError(
            'email',
            'This user is already a member of your workspace.'
          );
        } else if (err.code === 'INVITATION_ALREADY_PENDING') {
          setFieldError(
            'email',
            'An invitation is already pending for this email address.'
          );
        } else {
          setFieldError('email', 'A conflict occurred. Please try again.');
        }
      } else if (err?.status === 403) {
        setFieldError(
          'applicationRole',
          'You do not have permission to assign this role.'
        );
      } else if (err?.status === 400) {
        setFieldError('email', err.message ?? 'Invalid request.');
      } else {
        setFieldError(
          'email',
          'An unexpected error occurred. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues: AddUserFormValues = { email: '', applicationRole: '' };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white shadow-xl p-6">
              <Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">
                Add User to Workspace
              </Dialog.Title>

              <Formik
                initialValues={initialValues}
                validationSchema={emailSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form
                    className="space-y-4"
                    placeholder={undefined}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  >
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email address <span className="text-red-500">*</span>
                      </label>
                      <Field
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="off"
                        placeholder="user@example.com"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <ErrorMessage
                        name="email"
                        component="p"
                        className="mt-1 text-xs text-red-600"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="applicationRole"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Application role{' '}
                        <span className="text-gray-400 text-xs">
                          (optional — defaults applied if blank)
                        </span>
                      </label>
                      <Field
                        as="select"
                        id="applicationRole"
                        name="applicationRole"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Use default role(s)</option>
                        {roleOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="applicationRole"
                        component="p"
                        className="mt-1 text-xs text-red-600"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md text-sm font-medium text-gray-700
                                   border border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2 rounded-md text-sm font-medium text-white
                                   bg-green-600 hover:bg-green-700 disabled:opacity-60
                                   transition-colors"
                      >
                        {isSubmitting ? 'Adding…' : 'Add User'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddUserModal;
